import { useEffect, useRef, useState } from "react";
import { baseApi, useCheckJobStatusQuery } from "../../../../features/_baseQuery";
import { useRemoveCompanyBulkMutation as useRemoveCompaniesBulkMutation } from "../../../../features/companyApi";
import { useDeleteBulkTargetContactsMutation } from "../../../../features/contactsApi";
import { useRemoveBulkPeopleMutation } from "../../../../features/peopleApi";
import { addBannerWithTimeout } from "../../../lib/centralizedBanner/slice";
import { ContactType, FindAllContactsFilters } from "../../../lib/filters/dto";
import { initialStateContactsFilters } from "../../../lib/filters/filtersSlice";
import { useAppDispatch } from "../../../lib/redux/hooks";
import { DatabaseSection, type TargetSection } from "../../../lib/utils";
import { ConfirmModal } from "../../../ui/organisms/confirmModal.tsx";

export function DeleteBulkContacts({ filters, setFilters, showModal, setShowModal, contactType, section, targetId }: { filters: FindAllContactsFilters, setFilters: (filters: FindAllContactsFilters) => void, showModal: boolean, setShowModal: (show: boolean) => void, contactType: ContactType, section: DatabaseSection | TargetSection, targetId?: string }) {
    const dispatch = useAppDispatch();

    const [jobIdPeople, setJobIdPeople] = useState<string | null>(null);
    const [isPollingPeople, setIsPollingPeople] = useState(false);
    const [jobIdCompanies, setJobIdCompanies] = useState<string | null>(null);
    const [isPollingCompanies, setIsPollingCompanies] = useState(false);
    const processedJobIds = useRef(new Set<string>());

    const [removeBulkPeople, {
        isLoading: isDeletingBulkPeople,
        reset: resetMutationBulk
    }] = useRemoveBulkPeopleMutation();

    const [removeBulkCompanies, {
        isLoading: isDeletingBulkCompanies,
        reset: resetMutationBulkCompanies
    }] = useRemoveCompaniesBulkMutation();

    const [deleteBulkTargetContacts, {
        isLoading: isDeletingBulkTargetContacts,
        reset: resetMutationBulkTargetContacts
    }] = useDeleteBulkTargetContactsMutation();

    const { data: jobStatusPeople } = useCheckJobStatusQuery(jobIdPeople!, {
        skip: !jobIdPeople || !isPollingPeople,
        pollingInterval: 4000,
    });

    const { data: jobStatusCompanies } = useCheckJobStatusQuery(jobIdCompanies!, {
        skip: !jobIdCompanies || !isPollingCompanies,
        pollingInterval: 4000,
    });

    const isLoading = isDeletingBulkPeople || isDeletingBulkCompanies || isDeletingBulkTargetContacts ||
        isPollingPeople || isPollingCompanies;

    useEffect(() => {
        return () => {
            resetMutationBulk();
            resetMutationBulkCompanies();
            resetMutationBulkTargetContacts();
            setIsPollingPeople(false);
            setJobIdPeople(null);
            setIsPollingCompanies(false);
            setJobIdCompanies(null);
            processedJobIds.current.clear();
        };
    }, [resetMutationBulk, resetMutationBulkCompanies, resetMutationBulkTargetContacts]);

    useEffect(() => {
        if (jobStatusPeople && (jobStatusPeople.status === 'COMPLETED' || jobStatusPeople.status === 'FAILED')) {
            if (processedJobIds.current.has(jobStatusPeople.id)) return;
            processedJobIds.current.add(jobStatusPeople.id);

            setIsPollingPeople(false);
            setJobIdPeople(null);

            if (jobStatusPeople.status === 'COMPLETED') {
                dispatch(baseApi.util.invalidateTags([{ type: 'Database', id: 'people' }]));
                setFilters({ ...initialStateContactsFilters[section][ContactType.PERSON] });
                handleCloseDeleteModal();
                dispatch(addBannerWithTimeout({
                    message: "Persone eliminate con successo",
                    variant: "success"
                }));
            } else {
                dispatch(addBannerWithTimeout({
                    message: jobStatusPeople.errorMessage || "Si è verificato un errore durante l'eliminazione",
                    variant: "error"
                }));
            }
        }
    }, [jobStatusPeople, dispatch, section, setFilters]);

    useEffect(() => {
        if (jobStatusCompanies && (jobStatusCompanies.status === 'COMPLETED' || jobStatusCompanies.status === 'FAILED')) {
            if (processedJobIds.current.has(jobStatusCompanies.id)) return;
            processedJobIds.current.add(jobStatusCompanies.id);

            setIsPollingCompanies(false);
            setJobIdCompanies(null);

            if (jobStatusCompanies.status === 'COMPLETED') {
                dispatch(baseApi.util.invalidateTags([{ type: 'Database', id: 'companies' }]));
                setFilters({ ...initialStateContactsFilters[section][ContactType.COMPANY] });
                handleCloseDeleteModal();
                dispatch(addBannerWithTimeout({
                    message: "Aziende eliminate con successo",
                    variant: "success"
                }));
            } else {
                dispatch(addBannerWithTimeout({
                    message: jobStatusCompanies.errorMessage || "Si è verificato un errore durante l'eliminazione",
                    variant: "error"
                }));
            }
        }
    }, [jobStatusCompanies, dispatch, section, setFilters]);

    const handleCloseDeleteModal = () => {
        setShowModal(false);
        resetMutationBulk();
        resetMutationBulkCompanies();
        resetMutationBulkTargetContacts();
    };

    const handleDeletePeopleConfirm = async () => {
        if (section === 'target') {
            try {
                await deleteBulkTargetContacts({ filters, targetList: targetId || "", contactType }).unwrap();
                setFilters({ ...initialStateContactsFilters[section][contactType] });
                handleCloseDeleteModal();
                dispatch(addBannerWithTimeout({
                    message: contactType === ContactType.PERSON ? "Persone eliminate con successo" : "Aziende eliminate con successo",
                    variant: "success"
                }));
            } catch (error: any) {
                setFilters({ ...initialStateContactsFilters[section][contactType] });
                handleCloseDeleteModal();
                dispatch(addBannerWithTimeout({
                    message: "Si è verificato un errore durante l'eliminazione",
                    variant: "error"
                }));
            }
            return;
        }
        if (contactType === ContactType.PERSON) {
            try {
                const response = await removeBulkPeople(filters).unwrap();
                setJobIdPeople(response.id);
                setIsPollingPeople(true);
            } catch (error: any) {
                setFilters({ ...initialStateContactsFilters[section][ContactType.PERSON] });
                handleCloseDeleteModal();
                dispatch(addBannerWithTimeout({
                    message: "Si è verificato un errore durante l'eliminazione",
                    variant: "error"
                }));
            }
        } else {
            try {
                const response = await removeBulkCompanies(filters).unwrap();
                setJobIdCompanies(response.id);
                setIsPollingCompanies(true);
            } catch (error: any) {
                setFilters({ ...initialStateContactsFilters[section][ContactType.COMPANY] });
                handleCloseDeleteModal();
                dispatch(addBannerWithTimeout({
                    message: "Si è verificato un errore durante l'eliminazione",
                    variant: "error"
                }));
            }
        }
    };

    return (
        <ConfirmModal
            key={"delete-bulk-people-modal"}
            loading={isLoading}
            title={contactType === ContactType.PERSON ? "Elimina persone" : "Elimina aziende"}
            label={contactType === ContactType.PERSON ? "Sei sicuro di voler eliminare tutte le persone trovate?" : "Sei sicuro di voler eliminare tutte le aziende trovate?"}
            confirmAction={handleDeletePeopleConfirm}
            onClose={handleCloseDeleteModal}
            visible={showModal}
        />
    )
}